import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ACTIVITY_LOG,
  CLAIM_BANNERS,
  STATUS_BADGE_CLASS,
  buildClaimTimeline,
  getClaimStatus,
} from "./claimData";
import "./claim.css";

/* Claim detail — ported from the prototype (view-claim.html).
   Status-aware: banner, badge, Edit visibility (hidden once Approved/Paid),
   payment status, and the numbered Approval Progress timeline all adapt to
   the claim status. Details / Logs tabs match the other detail pages. */

const LOG_BADGE = {
  info: "log-badge log-badge-info",
  amend: "log-badge log-badge-amend",
  reject: "log-badge log-badge-reject",
  muted: "log-badge log-badge-muted",
  approved: "status-badge claim-status-approved",
};

const ClaimDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const claimId = (id || "CL-2026-091").trim().replace("#", "");
  const status = getClaimStatus(claimId);
  const [activeTab, setActiveTab] = useState("details");

  const banner = CLAIM_BANNERS[status] || CLAIM_BANNERS["Pending Approval"];
  const timeline = useMemo(() => buildClaimTimeline(status), [status]);
  const editable = status !== "Approved" && status !== "Paid";
  const paymentStatus = status === "Paid" ? { label: "Paid", cls: "payment-paid" } : status === "Draft" ? { label: "—", cls: "payment-muted" } : { label: "Pending", cls: "payment-pending" };

  const editButton = editable && (
    <button type="button" className="btn btn-primary" onClick={() => navigate("/claim/create")}>
      <i className="ri-edit-line" /> Edit Claim
    </button>
  );

  return (
    <div className="bes-claim bes-claim-detail">
      <div className="claim-page-head">
        <div className="title-left">
          <div className="page-icon claim-icon">
            <i className="ri-file-list-3-line" />
          </div>
          <div>
            <h1>Claim #{claimId}</h1>
            <p>Cash Claim · Submitted on 20 July 2026</p>
          </div>
        </div>
        <div className="action-group">
          <button type="button" className="btn btn-outline" onClick={() => window.print()}>
            <i className="ri-printer-line" /> Print
          </button>
          {editButton}
        </div>
      </div>

      <div className={`status-banner ${banner.cls}`}>
        <i className={banner.icon} />
        <div>
          <strong>{banner.title}</strong>
          <p>{banner.text}</p>
        </div>
      </div>

      <div className="custom-tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === "details" ? "active" : ""}`}
          onClick={() => setActiveTab("details")}
        >
          Details
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === "logs" ? "active" : ""}`}
          onClick={() => setActiveTab("logs")}
        >
          Logs
        </button>
      </div>

      {activeTab === "details" ? (
        <div className="claim-layout">
          <div>
            {/* 1. Cash Claim Details */}
            <section className="card claim-details-card">
              <div className="card-header">
                <div className="view-claim-section-title">
                  <span>1</span>
                  <h2>Cash Claim Details</h2>
                </div>
                <span className={`status-badge ${STATUS_BADGE_CLASS[status] || "status-pending"}`}>
                  <i className="ri-circle-fill dot" /> {status}
                </span>
              </div>
              <div className="card-body">
                <div className="claim-primary-grid">
                  <div>
                    <span className="detail-label">Staff Name</span>
                    <div className="detail-value">Eldren Aniell</div>
                  </div>
                  <div>
                    <span className="detail-label">Claim Period</span>
                    <div className="detail-value">November 2026</div>
                  </div>
                  <div>
                    <span className="detail-label">Claim ID</span>
                    <div className="detail-value">#{claimId}</div>
                  </div>
                  <div>
                    <span className="detail-label">Submitted Date</span>
                    <div className="detail-value">20 July 2026, 10:35 AM</div>
                  </div>
                </div>
                <div className="request-context-row">
                  <div>
                    <span className="detail-label">Training Request</span>
                    <div className="detail-value">TR-2026-062 · Successful Project Management</div>
                  </div>
                  <div>
                    <span className="detail-label">Training Budget</span>
                    <div className="detail-value training-budget-value">RM 5,000.00 annual quota · Manager</div>
                  </div>
                </div>
                <div className="claim-remarks-row">
                  <span className="detail-label">Remarks / Purpose</span>
                  <div className="detail-value">
                    Staff welfare and medical-related expenses incurred for approved operational activities.
                  </div>
                </div>
              </div>
            </section>

            {/* 2. Claim Items */}
            <section className="card claim-items-card">
              <div className="card-header">
                <div className="view-claim-section-title">
                  <span>2</span>
                  <h2>Claim Items</h2>
                </div>
                <span className="header-note-light">3 items</span>
              </div>
              <div className="claim-items-table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="w-14">Date</th>
                      <th className="w-15">Reference No.</th>
                      <th>Description</th>
                      <th className="w-15 text-right">Amount (RM)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>03 Nov 2026</td>
                      <td>RCPT-1103-01</td>
                      <td>
                        <strong>Annual gym membership</strong>
                        <div className="item-note">Approved employee wellness benefit</div>
                      </td>
                      <td className="amount">3,600.00</td>
                    </tr>
                    <tr>
                      <td>12 Nov 2026</td>
                      <td>INV-DEN-2981</td>
                      <td>
                        <strong>Dental filling treatment</strong>
                        <div className="item-note">Employee medical claim</div>
                      </td>
                      <td className="amount">2,400.00</td>
                    </tr>
                    <tr>
                      <td>21 Nov 2026</td>
                      <td>MIL-1121-04</td>
                      <td>
                        <strong>Mileage reimbursement attachment</strong>
                        <div className="item-note">Operational travel and client visits</div>
                      </td>
                      <td className="amount">1,992.00</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} className="total-label">
                        Total Claim Amount
                      </td>
                      <td className="amount total-amount">7,992.00</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>

            {/* 3. Supporting Documents */}
            <section className="card">
              <div className="card-header">
                <div className="view-claim-section-title">
                  <span>3</span>
                  <h2>Supporting Documents</h2>
                </div>
              </div>
              <div className="card-body">
                {[
                  { name: "Gym_Membership_Receipt.pdf", meta: "PDF · 428 KB · Uploaded 20 Jul 2026", icon: "ri-file-pdf-2-line", action: "View", actionIcon: "ri-eye-line" },
                  { name: "Dental_Invoice_INV-DEN-2981.pdf", meta: "PDF · 316 KB · Uploaded 20 Jul 2026", icon: "ri-file-pdf-2-line", action: "View", actionIcon: "ri-eye-line" },
                  { name: "Mileage_Claim_November_2026.xlsx", meta: "XLSX · 94 KB · Uploaded 20 Jul 2026", icon: "ri-file-excel-2-line", action: "Download", actionIcon: "ri-download-line" },
                ].map((file) => (
                  <div className="attachment-row" key={file.name}>
                    <div className="attachment-info">
                      <div className="attachment-icon">
                        <i className={file.icon} />
                      </div>
                      <div>
                        <div className="attachment-name">{file.name}</div>
                        <div className="attachment-meta">{file.meta}</div>
                      </div>
                    </div>
                    <button type="button" className="btn btn-outline">
                      <i className={file.actionIcon} /> {file.action}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <aside>
            <div className="side-card">
              <h3>Claim Summary</h3>
              <div className="summary-row">
                <span>Subtotal</span>
                <strong>RM 7,992.00</strong>
              </div>
              <div className="summary-row">
                <span>Cash Advance Offset</span>
                <strong>RM 0.00</strong>
              </div>
              <div className="summary-row">
                <span>Amount Payable</span>
                <strong className="summary-total">RM 7,992.00</strong>
              </div>
            </div>

            <div className="side-card">
              <h3>Approval Progress</h3>
              <div className="timeline">
                {timeline.map((step) => (
                  <div className={`timeline-item ${step.state}`} key={step.title}>
                    <div className="timeline-dot">
                      {step.dot === "check" ? (
                        <i className="ri-check-line" />
                      ) : step.dot === "cross" ? (
                        <i className="ri-close-line" />
                      ) : (
                        step.dot
                      )}
                    </div>
                    <div>
                      <div className="timeline-title">{step.title}</div>
                      <div className="timeline-meta">{step.meta}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="side-card">
              <h3>Payment Details</h3>
              <div className="summary-row">
                <span>Bank</span>
                <strong>Maybank</strong>
              </div>
              <div className="summary-row">
                <span>Account Holder</span>
                <strong>Eldren Aniell</strong>
              </div>
              <div className="summary-row">
                <span>Account No.</span>
                <strong>•••• 4921</strong>
              </div>
              <div className="summary-row">
                <span>Payment Status</span>
                <strong className={paymentStatus.cls}>{paymentStatus.label}</strong>
              </div>
            </div>

            <div className="side-card">
              <h3>Audit Information</h3>
              <div className="summary-row">
                <span>Created</span>
                <strong>19 Jul 2026</strong>
              </div>
              <div className="summary-row">
                <span>Last Updated</span>
                <strong>20 Jul 2026</strong>
              </div>
              <div className="summary-row">
                <span>Version</span>
                <strong>1.0</strong>
              </div>
            </div>
          </aside>
        </div>
      ) : (
        <section className="card">
          <div className="card-header">
            <div className="view-claim-section-title">
              <h2>Activity Log</h2>
            </div>
            <span className="header-note-light">All changes, approvals, rejections, and amendments — newest first</span>
          </div>
          <div className="card-body no-pad">
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="w-17">Date / Time</th>
                    <th className="w-21">By</th>
                    <th className="w-14">Action</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {ACTIVITY_LOG.map((entry) => (
                    <tr key={`${entry.time}-${entry.action}`}>
                      <td className="text-muted">{entry.time}</td>
                      <td className="font-medium">
                        {entry.by} <small className="text-muted">({entry.role})</small>
                      </td>
                      <td>
                        <span className={LOG_BADGE[entry.badge] || LOG_BADGE.muted}>{entry.action}</span>
                      </td>
                      <td>{entry.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      <div className="action-bar">
        <Link to="/claim" className="btn btn-outline">
          <i className="ri-arrow-left-line" /> Back to Claims
        </Link>
        <div className="action-group">
          <button type="button" className="btn btn-outline" onClick={() => window.print()}>
            <i className="ri-printer-line" /> Print
          </button>
          {editButton}
        </div>
      </div>
    </div>
  );
};

export default ClaimDetailPage;
